import { doc, getDoc, getDocFromServer } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/config";
import { TRANSACTION_ENDPOINT } from "../../mev/constants/requestConstant";
import { recordRqExchange } from "../rqExchangeLog";
import { logAPIResponse } from "../../utils/backgroundLogger";

export type CloudMevWorkerSettings = {
  workerUrl: string;
  configured: boolean;
  hasSecret: boolean;
};

export type CloudMevCertInfo = {
  name: string;
  serial: string;
  expiry: string;
  error: string;
  nickname: string;
  hasPsi: boolean;
  fullText?: string;
  idapprl?: string;
};

export type CloudMevUserReportRecord = {
  id: string;
  at: string;
  userName: string;
  userId: string;
  year: number;
  scope: string;
  orderCount: number;
  sent: boolean;
  noDoc?: string;
  reportData?: Record<string, string>;
  exchange?: {
    at: string;
    url: string;
    status: number;
    headers?: Record<string, string>;
    request?: unknown;
    response?: unknown;
  };
};

export type CloudMevUser = {
  nomUtil: string;
  noTPS: string;
  noTVQ: string;
  createdAt?: string;
};

export type CloudMevTerminal = {
  id: string;
  name: string;
  deviceId: string;
  timestamp: string;
  env: string;
};

export type CloudMevLog = {
  id: string;
  timestamp: string;
  tab: string;
  env: string;
  action: string;
  data: string;
  isError: boolean;
};

export type CloudMevDebugExchange = {
  id: string;
  timestamp: string;
  env: string;
  operation: string;
  directionPair?: boolean;
  url: string;
  status: number;
  isError?: boolean;
  request: { headers?: Record<string, string>; body?: any };
  response: { body?: any };
};

export type CloudMevTransactionEvent =
  | "temporary"
  | "final"
  | "cancel_before_print"
  | "cancel_after_print"
  | "refund"
  | "correct_payment"
  | "reprint";

export type CloudMevSnapshot = {
  status: string;
  idApprl: string;
  lastError: string;
  lastEnrollAt: string | null;
  updatedAt: string | null;
  pendingCsrPem: string;
  lastCsrPem: string;
  lastCsrUsedAt: string | null;
  lastCsrUsedFor: string;
  pendingCsrAt: string | null;
  businessLocked: boolean;
  business: Record<string, string>;
  csrForm: Record<string, string>;
  cert: CloudMevCertInfo;
  users: CloudMevUser[];
  terminals: CloudMevTerminal[];
  logs: CloudMevLog[];
  userReportHistory?: CloudMevUserReportRecord[];
  debugExchanges: CloudMevDebugExchange[];
  transHeader: Record<string, string>;
  headers: Record<string, string>;
  preced?: string;
  lastNoTrans?: string;
};

export type CloudMevStoreStatus = {
  status: string;
  idApprl: string;
  envirn: string;
  lastError: string;
  lastEnrollAt: string | null;
  updatedAt: string | null;
  snapshot?: CloudMevSnapshot;
};

export type CloudMevPingResult = {
  ok: boolean;
  error?: string;
  service?: string;
};

export type CloudMevEnrollInput = {
  storeId: string;
  envirn: string;
  userId: string;
  authCode: string;
  qstNumber: string;
  gstNumber: string;
  dossierNumber: string;
  activitySubsector: string;
  timezone: string;
  nickname: string;
};

export type CloudMevEnrollResult = {
  ok: boolean;
  status?: string;
  idApprl?: string;
  serial?: string;
  expiry?: string;
  envirn?: string;
  cert?: CloudMevCertInfo;
  privateKeyPem?: string;
  certPem?: string;
  certifPsi?: string;
  rqBody?: unknown;
  rqResponse?: unknown;
  rqStatus?: number;
};

export function isPlaceholderIdapprl(value?: string) {
  const next = String(value || "").trim();
  if (!next) return true;
  const compact = next.replace(/[^A-Za-z0-9]/g, "");
  return !compact || /^0+$/.test(compact) || next === "0000000000000001";
}

export function pickIssuedIdapprl(...values: unknown[]): string {
  for (const value of values) {
    const next = String(value || "").trim();
    if (!isPlaceholderIdapprl(next)) return next;
  }
  return "";
}

export function issuedIdentityFromResult(result: unknown): { idApprl: string; cert: CloudMevCertInfo } {
  const data = (result || {}) as CloudMevEnrollResult & {
    retourCertif?: Record<string, unknown>;
  };
  const block = ((data.rqResponse as any)?.retourCertif ||
    (data.rqBody as any)?.retourCertif ||
    data.retourCertif ||
    {}) as Record<string, string>;
  const cert = data.cert || ({} as CloudMevCertInfo);
  const idApprl = String(data.idApprl || cert.idapprl || block.idApprl || block.idapprl || "").trim();
  return {
    idApprl,
    cert: {
      name: cert.name || "",
      serial: String(data.serial || cert.serial || block.serial || block.noSerie || ""),
      expiry: String(data.expiry || cert.expiry || ""),
      error: cert.error || "",
      nickname: cert.nickname || "",
      hasPsi: Boolean(cert.hasPsi || block.certifPSI || block.certifPsi),
      fullText: cert.fullText || "",
      idapprl: idApprl,
    },
  };
}

function callable<TReq, TRes>(name: string) {
  return httpsCallable<TReq, TRes>(functions, name);
}

const CSR_BEGIN = "-----BEGIN CERTIFICATE REQUEST-----";
const CSR_END = "-----END CERTIFICATE REQUEST-----";

export function sanitizeCsrPem(value: unknown): string {
  const chunks: string[] = [];
  let current: unknown = value;
  for (let i = 0; i < 64; i++) {
    if (current == null) break;
    if (typeof current === "object" && !Array.isArray(current)) {
      const record = current as Record<string, unknown>;
      current = record.pem ?? record.csrPem ?? record.csr ?? record.lastCsrPem ?? record.pendingCsrPem ?? "";
      continue;
    }
    const text = String(current);
    chunks.push(text);
    const trimmed = text.trim();
    if (!trimmed) break;
    if (trimmed.startsWith("{") || trimmed.startsWith("\"") || trimmed.startsWith("[")) {
      try {
        current = JSON.parse(trimmed);
        continue;
      } catch {
        const unescaped = trimmed.replace(/\\"/g, "\"").replace(/\\n/g, "\n").replace(/\\\\/g, "\\");
        if (unescaped !== trimmed) {
          current = unescaped;
          continue;
        }
      }
    }
    current = trimmed.replace(/\\n/g, "\n");
    chunks.push(String(current));
    break;
  }

  const extract = (text: string) => {
    let haystack = text;
    for (let i = 0; i < 32; i++) {
      const start = haystack.indexOf(CSR_BEGIN);
      const end = haystack.indexOf(CSR_END);
      if (start >= 0 && end > start) {
        const body = haystack
          .slice(start + CSR_BEGIN.length, end)
          .replace(/\\[nr]/g, "")
          .replace(/\\+/g, "")
          .replace(/\s+/g, "");
        if (!body) return "";
        return [CSR_BEGIN, body, CSR_END].join("\n");
      }
      const next = haystack.replace(/\\"/g, "\"").replace(/\\n/g, "\n").replace(/\\\\/g, "\\");
      if (next === haystack) break;
      haystack = next;
    }
    return "";
  };

  for (let i = chunks.length - 1; i >= 0; i--) {
    const pem = extract(chunks[i]);
    if (pem) return pem;
  }
  return extract(typeof value === "string" ? value : JSON.stringify(value ?? ""));
}

function extractCsrMeta(value: unknown): { generatedAt: string; usedAt: string; usedFor: "" | "add" | "replace" } {
  let generatedAt = "";
  let usedAt = "";
  let usedFor: "" | "add" | "replace" = "";
  let current: unknown = value;
  for (let i = 0; i < 64; i++) {
    if (current == null) break;
    if (typeof current === "string") {
      const text = current.trim();
      if (!text.startsWith("{") && !text.startsWith("\"")) break;
      try {
        current = JSON.parse(text);
        continue;
      } catch {
        break;
      }
    }
    if (typeof current !== "object" || Array.isArray(current)) break;
    const record = current as Record<string, unknown>;
    if (!generatedAt && record.generatedAt) generatedAt = String(record.generatedAt);
    if (!usedAt && record.usedAt) usedAt = String(record.usedAt);
    if (!usedFor && (record.usedFor === "add" || record.usedFor === "replace")) usedFor = record.usedFor;
    current = record.pem ?? record.csrPem ?? record.csr ?? "";
  }
  return { generatedAt, usedAt, usedFor };
}

export function csrHasRestaurantOrganization(pem: string): boolean {
  const body = sanitizeCsrPem(pem)
    .replace(CSR_BEGIN, "")
    .replace(CSR_END, "")
    .replace(/\s+/g, "");
  if (!body) return false;
  try {
    return atob(body).includes("Restaurant-");
  } catch {
    return false;
  }
}

export function formatCloudMevServerResponse(value: unknown): string {
  if (value == null) return "";
  const err = value as {
    details?: unknown;
    customData?: { details?: unknown; message?: string };
    message?: string;
    code?: string;
    rqBody?: unknown;
    rqResponse?: unknown;
    rqStatus?: number;
    status?: number;
  };
  const details = (err.details ?? err.customData?.details) as
    | { rqBody?: unknown; status?: number; message?: string }
    | string
    | undefined;
  const rqBody =
    err.rqResponse ??
    err.rqBody ??
    (details && typeof details === "object" ? details.rqBody ?? details : undefined);
  const status = err.rqStatus ?? err.status ?? (details && typeof details === "object" ? details.status : undefined);
  const payload =
    rqBody !== undefined
      ? status != null
        ? { status, response: rqBody }
        : rqBody
      : typeof details === "string" && details.trim()
        ? details
        : err.message || err.code
          ? { message: err.message, code: err.code, details: details ?? null }
          : value;
  try {
    return typeof payload === "string" ? payload : JSON.stringify(payload, null, 2);
  } catch {
    return String(value);
  }
}

function hideInternalStoreId(text: string) {
  return String(text || "").replace(/\b[A-Za-z0-9_-]{16,}\/(DEV|ESSAI|PROD)\b/gi, "this store");
}

export function cloudMevErrorMessage(error: unknown, fallback = "Operation failed") {
  const err = error as {
    message?: string;
    details?: unknown;
    customData?: { message?: string; details?: unknown };
  };
  const details = err?.details ?? err?.customData?.details;
  if (typeof details === "string" && details.trim()) return hideInternalStoreId(details.trim());
  if (details && typeof details === "object") {
    const nested = details as { message?: string; rqBody?: { erreurEnrolement?: { msgErreur?: string } } };
    const rqMsg = String(nested.rqBody?.erreurEnrolement?.msgErreur || nested.message || "").trim();
    if (rqMsg && !/^(internal|unknown)$/i.test(rqMsg)) return hideInternalStoreId(rqMsg);
  }
  const custom = String(err?.customData?.message || "").trim();
  if (custom && !/^(internal|unknown|functions\/internal)$/i.test(custom)) return hideInternalStoreId(custom);
  const message = String(err?.message || "").replace(/^FirebaseError:\s*/i, "").replace(/^functions\//, "").trim();
  if (message && !/^(internal|unknown)$/i.test(message)) return hideInternalStoreId(message);
  return fallback;
}

function normalizeEnv(value?: string): "DEV" | "ESSAI" | "PROD" {
  const next = String(value || "DEV").trim().toUpperCase();
  if (next === "PROD" || next === "ESSAI") return next;
  return "DEV";
}

function pickWorkerUrl(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== "string") continue;
    let next = value.trim();
    if (!next) continue;
    if (!/^https?:\/\//i.test(next)) next = `https://${next}`;
    if (next.toLowerCase().startsWith("http://")) next = `https://${next.slice(7)}`;
    return next.replace(/\/+$/, "");
  }
  return "";
}

function bucketScore(envData: any): number {
  if (!envData || typeof envData !== "object") return -1;
  const cert = envData.cert || {};
  let score = 0;
  if (cert.serial || cert.name || cert.fullText || cert.nickname) score += 8;
  if (envData.idApprl && envData.idApprl !== "0000-0000-0000") score += 4;
  if (String(envData.status || "") === "enrolled") score += 2;
  if (envData.lastCsrPem || envData.pendingCsrPem) score += 2;
  if (envData.lastCsrUsedAt || envData.lastEnrollAt) score += 1;
  return score;
}

function envBucketFromDoc(data: any, env: string) {
  const nested = data?.envs?.[env];
  const dotted = data?.[`envs.${env}`];
  return bucketScore(nested) >= bucketScore(dotted) ? nested || {} : dotted || {};
}

export function serializeCloudMevError(error: unknown) {
  const err = (error || {}) as {
    message?: string;
    code?: string;
    details?: unknown;
    customData?: { details?: unknown; message?: string };
  };
  return {
    message: cloudMevErrorMessage(error, err.message || "Cloud MEV request failed"),
    code: err.code || "",
    details: err.details ?? err.customData?.details ?? null,
  };
}

function recordTransactionExchange(
  sent: { reqTrans?: unknown; headers?: Record<string, string> },
  result: { rqUrl?: string; rqStatus?: number; rqRequest?: unknown; rqResponse?: unknown; rqHeaders?: Record<string, string> } | null,
  error?: unknown,
) {
  const details = (error ? (serializeCloudMevError(error).details || {}) : {}) as {
    rqUrl?: string;
    rqStatus?: number;
    rqHeaders?: Record<string, string>;
    rqRequest?: unknown;
    rqResponse?: unknown;
    message?: string;
  };
  const returned = result?.rqResponse ?? details.rqResponse ?? (error ? { message: serializeCloudMevError(error).message } : null);
  recordRqExchange({
    url: String(result?.rqUrl || details.rqUrl || TRANSACTION_ENDPOINT),
    status: result?.rqStatus ?? details.rqStatus ?? null,
    requestHeaders: result?.rqHeaders || details.rqHeaders || sent.headers,
    request: result?.rqRequest || details.rqRequest || { reqTrans: sent.reqTrans },
    response: returned,
  });
}

export async function findEnrolledCloudMevEnv(storeId: string, preferred = "DEV") {
  if (!storeId) return null;
  const snap = await getDoc(doc(db, "cloudMevState", storeId));
  if (!snap.exists()) return null;
  const data = snap.exists() ? snap.data() || {} : {};
  const order = [normalizeEnv(preferred), "DEV", "ESSAI", "PROD"].filter(
    (value, index, list) => list.indexOf(value) === index
  );
  for (const env of order) {
    const envData = envBucketFromDoc(data, env);
    const idApprl = pickIssuedIdapprl(
      envData.idApprl,
      envData.cert?.idapprl,
      envData.transHeader?.IDAPPRL,
      envData.headers?.IDAPPRL,
      envData.headers?.idapprl
    );
    if (envData.status === "enrolled" || !isPlaceholderIdapprl(idApprl)) {
      return { envirn: env, idApprl };
    }
  }
  return null;
}

export function certLooksInstalled(cert?: CloudMevCertInfo | null) {
  if (!cert) return false;
  return Boolean(cert.name || cert.serial || cert.fullText || cert.nickname);
}

function toIso(value: any): string | null {
  if (!value) return null;
  if (typeof value === "string") return value;
  if (value instanceof Date) return value.toISOString();
  if (typeof value.toDate === "function") return value.toDate().toISOString();
  return null;
}

function emptySnapshot(): CloudMevSnapshot {
  return {
    status: "",
    idApprl: "",
    lastError: "",
    lastEnrollAt: null,
    updatedAt: null,
    pendingCsrPem: "",
    lastCsrPem: "",
    lastCsrUsedAt: null,
    lastCsrUsedFor: "",
    pendingCsrAt: null,
    businessLocked: true,
    business: {},
    csrForm: {},
    cert: { name: "", serial: "", expiry: "", error: "", nickname: "", hasPsi: false, fullText: "", idapprl: "" },
    users: [],
    terminals: [],
    logs: [],
    userReportHistory: [],
    debugExchanges: [],
    transHeader: {},
    headers: {},
    preced: "",
    lastNoTrans: "",
  };
}

export async function getCloudMevWorkerSettings(): Promise<CloudMevWorkerSettings> {
  const [systemSnap, publicSnap] = await Promise.all([
    getDoc(doc(db, "systemConfig", "cloudMevWorker")),
    getDoc(doc(db, "cloudMev", "worker")),
  ]);
  const system = systemSnap.exists() ? systemSnap.data() || {} : {};
  const published = publicSnap.exists() ? publicSnap.data() || {} : {};
  const workerUrl = pickWorkerUrl(published.workerUrl, system.workerUrl);
  const hasSecret = Boolean(published.hasSecret || system.hasSecret || system.workerSecret);
  return {
    workerUrl,
    configured: Boolean(workerUrl && hasSecret),
    hasSecret,
  };
}

export async function saveCloudMevWorkerSettings(payload: {
  workerUrl: string;
  workerSecret?: string;
}): Promise<CloudMevWorkerSettings> {
  const fn = callable<typeof payload, CloudMevWorkerSettings>("saveCloudMevWorkerSettings");
  const result = await fn(payload);
  return result.data;
}

export async function pingCloudMevWorker(): Promise<CloudMevPingResult> {
  const settings = await getCloudMevWorkerSettings();
  const workerUrl = pickWorkerUrl(settings.workerUrl);
  if (!workerUrl) {
    return { ok: false, error: "Cloud MEV worker is not configured" };
  }

  const healthUrl = `${workerUrl}/health`;
  try {
    const response = await fetch(healthUrl, { method: "GET" });
    const body = (await response.json().catch(() => ({}))) as { ok?: boolean; error?: string; service?: string };
    if ((response.ok && body.ok) || response.status === 401) {
      return { ok: true, service: body.service || "mev-csr" };
    }
    return {
      ok: false,
      error: body.error || `Worker health failed (${response.status})`,
    };
  } catch {
    try {
      await fetch(healthUrl, { method: "GET", mode: "no-cors" });
      return { ok: true, service: "mev-csr" };
    } catch (error: any) {
      return { ok: false, error: error?.message || "Worker ping failed" };
    }
  }
}

export async function enrollCloudMevCertificate(
  payload: CloudMevEnrollInput
): Promise<CloudMevEnrollResult> {
  const fn = callable<CloudMevEnrollInput, CloudMevEnrollResult>("enrollCloudMevCertificate");
  const result = await fn(payload);
  return result.data;
}

export async function getCloudMevStoreStatus(
  storeId: string,
  envirn = "DEV",
  options?: { fromServer?: boolean }
): Promise<CloudMevStoreStatus> {
  const env = normalizeEnv(envirn);
  if (!storeId) {
    return {
      status: "",
      idApprl: "",
      envirn: env,
      lastError: "",
      lastEnrollAt: null,
      updatedAt: null,
      snapshot: emptySnapshot(),
    };
  }

  const ref = doc(db, "cloudMevState", storeId);
  const snap = options?.fromServer
    ? await getDocFromServer(ref).catch(() => getDoc(ref))
    : await getDoc(ref);
  const data = snap.exists() ? snap.data() || {} : {};
  const envData = envBucketFromDoc(data, env);
  const fallback = emptySnapshot();
  const idApprl = pickIssuedIdapprl(
    envData.idApprl,
    envData.cert?.idapprl,
    envData.transHeader?.IDAPPRL,
    envData.headers?.IDAPPRL,
    envData.headers?.idapprl
  );
  const snapshot: CloudMevSnapshot = {
    ...fallback,
    ...envData,
    idApprl,
    business: { ...fallback.business, ...(envData.business || {}) },
    csrForm: { ...fallback.csrForm, ...(envData.csrForm || {}) },
    cert: {
      ...fallback.cert,
      ...(envData.cert || {}),
      idapprl: pickIssuedIdapprl(envData.cert?.idapprl, idApprl),
    },
    headers: {
      ...fallback.headers,
      ...(envData.headers || {}),
      IDAPPRL: pickIssuedIdapprl(envData.headers?.IDAPPRL, envData.headers?.idapprl, idApprl) || "0000-0000-0000",
      idapprl: pickIssuedIdapprl(envData.headers?.idapprl, envData.headers?.IDAPPRL, idApprl) || "0000-0000-0000",
    },
    transHeader: {
      ...(envData.transHeader || {}),
      IDAPPRL: pickIssuedIdapprl(envData.transHeader?.IDAPPRL, idApprl) || envData.transHeader?.IDAPPRL || "",
    },
    users: Array.isArray(envData.users) ? envData.users : [],
    terminals: Array.isArray(envData.terminals) ? envData.terminals : [],
    logs: Array.isArray(envData.logs) ? envData.logs : [],
    userReportHistory: Array.isArray(envData.userReportHistory) ? envData.userReportHistory : [],
    debugExchanges: Array.isArray(envData.debugExchanges) ? envData.debugExchanges : [],
    preced: envData.preced || "",
    lastNoTrans: envData.lastNoTrans || "",
    lastEnrollAt: toIso(envData.lastEnrollAt),
    pendingCsrPem: sanitizeCsrPem(envData.pendingCsrPem || ""),
    lastCsrPem: sanitizeCsrPem(envData.lastCsrPem || envData.pendingCsrPem || ""),
    lastCsrUsedAt: toIso(envData.lastCsrUsedAt),
    lastCsrUsedFor: String(envData.lastCsrUsedFor || ""),
    pendingCsrAt: toIso(envData.pendingCsrAt),
    updatedAt: toIso(envData.updatedAt),
  };

  return {
    status: snapshot.status || "",
    idApprl,
    envirn: env,
    lastError: snapshot.lastError || "",
    lastEnrollAt: snapshot.lastEnrollAt,
    updatedAt: snapshot.updatedAt,
    snapshot,
  };
}

export async function saveCloudMevBusiness(payload: {
  storeId: string;
  envirn: string;
  business: Record<string, string>;
}) {
  const fn = callable<typeof payload, { ok: boolean }>("saveCloudMevBusiness");
  const result = await fn(payload);
  return result.data;
}

export async function createCloudMevCsr(payload: CloudMevEnrollInput) {
  const fn = callable<CloudMevEnrollInput, { ok: boolean; csrPem: string; organization?: string }>("createCloudMevCsr");
  const result = await fn(payload);
  return result.data;
}

export async function addCloudMevCertificate(payload: {
  storeId: string;
  envirn: string;
  csr?: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<typeof payload, CloudMevEnrollResult>("addCloudMevCertificate");
  const result = await fn(payload);
  return result.data;
}

export async function replaceCloudMevCertificate(payload: {
  storeId: string;
  envirn: string;
  csr?: string;
  serial?: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<typeof payload, CloudMevEnrollResult>("replaceCloudMevCertificate");
  const result = await fn(payload);
  return result.data;
}

export async function deleteCloudMevCertificate(payload: {
  storeId: string;
  envirn: string;
  serial: string;
  headers?: Record<string, string>;
  recordOnly?: boolean;
}) {
  const fn = callable<typeof payload, { ok: boolean; rqBody?: unknown; rqResponse?: unknown }>("deleteCloudMevCertificate");
  const result = await fn(payload);
  return result.data;
}

export async function addCloudMevUser(payload: {
  storeId: string;
  envirn: string;
  nomUtil: string;
  noTPS?: string;
  noTVQ?: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<typeof payload, { ok: boolean; users: CloudMevUser[]; rqBody?: unknown; rqResponse?: unknown }>("addCloudMevUser");
  const result = await fn(payload);
  return result.data;
}

export async function deleteCloudMevUser(payload: {
  storeId: string;
  envirn: string;
  nomUtil: string;
  noTPS?: string;
  noTVQ?: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<typeof payload, { ok: boolean; users: CloudMevUser[]; rqBody?: unknown; rqResponse?: unknown }>("deleteCloudMevUser");
  const result = await fn(payload);
  return result.data;
}

export async function addCloudMevTerminal(payload: {
  storeId: string;
  envirn: string;
  name: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<typeof payload, { ok: boolean; terminal: CloudMevTerminal }>("addCloudMevTerminal");
  const result = await fn(payload);
  return result.data;
}

export async function deleteCloudMevTerminal(payload: {
  storeId: string;
  envirn: string;
  terminalId: string;
}) {
  const fn = callable<typeof payload, { ok: boolean; terminals: CloudMevTerminal[] }>("deleteCloudMevTerminal");
  const result = await fn(payload);
  return result.data;
}

function showUserReportExchange(result: {
  ok?: boolean;
  rqUrl?: string;
  rqStatus?: number;
  rqHeaders?: Record<string, string>;
  rqRequest?: unknown;
  rqResponse?: unknown;
  rqBody?: unknown;
} | null, error?: unknown) {
  const details = (error ? (serializeCloudMevError(error).details || {}) : {}) as {
    rqUrl?: string;
    rqStatus?: number;
    rqHeaders?: Record<string, string>;
    rqRequest?: unknown;
    rqResponse?: unknown;
  };
  const request = result?.rqRequest || details.rqRequest;
  const response = result?.rqResponse || result?.rqBody || details.rqResponse;
  if (!request && !response && !error) return;
  const responseBody = response || (error ? { message: serializeCloudMevError(error).message } : null);
  recordRqExchange({
    url: String(result?.rqUrl || details.rqUrl || ""),
    status: result?.rqStatus ?? details.rqStatus ?? null,
    requestHeaders: result?.rqHeaders || details.rqHeaders,
    request,
    response: responseBody,
  });
  void logAPIResponse(
    String(result?.rqUrl || details.rqUrl || "WEB-SRM document"),
    "POST",
    request,
    responseBody,
    0,
    result?.rqStatus ?? details.rqStatus,
  );
}

export async function generateCloudMevUserReport(payload: {
  storeId: string;
  envirn: string;
  year?: number;
  scope?: "single" | "all";
  userName?: string;
  userId?: string;
  loggedInAt?: string;
  submit?: boolean;
  posUserId?: string;
  deleteHistoryId?: string;
  idApprl?: string;
  headers?: Record<string, string>;
}) {
  const fn = callable<
    typeof payload,
    {
      ok: boolean;
      reportData?: Record<string, string>;
      documentString?: string;
      orderCount?: number;
      historyId?: string;
      history?: CloudMevUserReportRecord[];
      rqStatus?: number;
      rqUrl?: string;
      rqHeaders?: Record<string, string>;
      rqRequest?: unknown;
      rqResponse?: unknown;
      rqBody?: unknown;
    }
  >("generateCloudMevUserReport");
  try {
    const result = await fn(payload);
    if (!payload.deleteHistoryId) await showUserReportExchange(result.data);
    return result.data;
  } catch (error) {
    await showUserReportExchange(null, error);
    throw error;
  }
}

export async function submitCloudMevUserReport(payload: {
  storeId: string;
  envirn: string;
  documentString: string;
  headers?: Record<string, string>;
  historyId?: string;
  userName?: string;
  userId?: string;
}) {
  const fn = callable<typeof payload, {
    ok: boolean;
    rqBody?: any;
    rqUrl?: string;
    rqStatus?: number;
    rqHeaders?: Record<string, string>;
    rqRequest?: unknown;
    rqResponse?: unknown;
    history?: CloudMevUserReportRecord[];
  }>("submitCloudMevUserReport");
  try {
    const result = await fn(payload);
    await showUserReportExchange(result.data);
    return result.data;
  } catch (error) {
    await showUserReportExchange(null, error);
    throw error;
  }
}

export async function sendCloudMevTransaction(payload: {
  storeId: string;
  envirn: string;
  event: CloudMevTransactionEvent;
  order: Record<string, unknown>;
  extras?: Record<string, unknown>;
  headers?: Record<string, string>;
  idApprl?: string;
  business?: Record<string, string>;
}) {
  const fn = callable<
    typeof payload,
    {
      ok: boolean;
      event: CloudMevTransactionEvent;
      rqHeaders?: Record<string, string>;
      rqRequest?: any;
      rqResponse?: any;
      rqStatus?: number;
      rqUrl?: string;
      rqBody?: any;
      qrCodeUrl?: string;
      exchanges?: any[];
    }
  >("sendCloudMevTransaction");
  try {
    const result = await fn(payload);
    recordTransactionExchange(
      { reqTrans: (payload.order as { reqTrans?: unknown })?.reqTrans, headers: payload.headers },
      result.data,
    );
    return result.data;
  } catch (error) {
    recordTransactionExchange(
      { reqTrans: (payload.order as { reqTrans?: unknown })?.reqTrans, headers: payload.headers },
      null,
      error,
    );
    throw error;
  }
}

export async function postPreparedCloudMevSale(payload: {
  storeId: string;
  envirn: string;
  reqTrans: Record<string, unknown>;
  headers?: Record<string, string>;
}) {
  const fn = callable<
    typeof payload,
    { ok: boolean; rqStatus: number; rqResponse?: any; rqUrl?: string; rqRequest?: any; emprCertifSEV?: string; timings?: { wrapMs: number; loadMs: number; rqMs: number; totalMs: number; skippedProductLookup: boolean } }
  >("postPreparedCloudMevSale");
  try {
    const result = await fn(payload);
    recordTransactionExchange({ reqTrans: payload.reqTrans, headers: payload.headers }, result.data);
    return result.data;
  } catch (error) {
    recordTransactionExchange({ reqTrans: payload.reqTrans, headers: payload.headers }, null, error);
    throw error;
  }
}

export async function sendCloudMevTransactionBatch(payload: {
  storeId: string;
  envirn: string;
  current?: { queueId?: number; orderId?: string; event?: string; order?: Record<string, unknown>; createdAt?: number; reqTrans?: Record<string, unknown> };
  queued?: Array<{ queueId?: number; orderId?: string; event?: string; order?: Record<string, unknown>; createdAt?: number; reqTrans?: Record<string, unknown> }>;
  headers?: Record<string, string>;
  idApprl?: string;
  business?: Record<string, string>;
}) {
  const fn = callable<
    typeof payload,
    {
      ok: boolean;
      rqRequest?: any;
      rqResponse?: any;
      rqStatus?: number;
      rqUrl?: string;
      qrCodeUrl?: string;
      acceptedQueueIds?: number[];
      currentOrderId?: string | null;
      event?: string;
    }
  >("sendCloudMevTransactionBatch");
  try {
    const result = await fn(payload);
    recordTransactionExchange(
      { reqTrans: payload.current?.reqTrans, headers: payload.headers },
      result.data,
    );
    return result.data;
  } catch (error) {
    recordTransactionExchange(
      { reqTrans: payload.current?.reqTrans, headers: payload.headers },
      null,
      error,
    );
    throw error;
  }
}

export type CloudMevSavedResponse = {
  id: string;
  at: string;
  label: string;
  text: string;
};

function uiStorageKey(kind: "csr" | "responses" | "cert", storeId: string, envirn: string) {
  return `cloudMev:${kind}:${storeId}:${String(envirn || "DEV").toUpperCase()}`;
}

function envStamp(envirn: string) {
  return String(envirn || "DEV").trim().toUpperCase();
}

function recordMatchesEnv(record: unknown, envirn: string) {
  if (!record || typeof record !== "object" || Array.isArray(record)) return false;
  return envStamp((record as { envirn?: string }).envirn) === envStamp(envirn);
}

export type CloudMevSavedCert = {
  idApprl: string;
  status: string;
  lastEnrollAt: string;
  cert: CloudMevCertInfo;
};

function emptySavedCert(): CloudMevSavedCert {
  return {
    idApprl: "",
    status: "",
    lastEnrollAt: "",
    cert: { name: "", serial: "", expiry: "", error: "", nickname: "", hasPsi: false, fullText: "", idapprl: "" },
  };
}

export function loadSavedCloudMevCertRecord(storeId: string, envirn: string): CloudMevSavedCert {
  if (!storeId || typeof localStorage === "undefined") return emptySavedCert();
  try {
    const parsed = JSON.parse(localStorage.getItem(uiStorageKey("cert", storeId, envirn)) || "{}");
    if (!recordMatchesEnv(parsed, envirn)) return emptySavedCert();
    const cert = parsed?.cert || {};
    return {
      idApprl: String(parsed?.idApprl || cert.idapprl || ""),
      status: String(parsed?.status || ""),
      lastEnrollAt: String(parsed?.lastEnrollAt || ""),
      cert: {
        name: String(cert.name || ""),
        serial: String(cert.serial || ""),
        expiry: String(cert.expiry || ""),
        error: String(cert.error || ""),
        nickname: String(cert.nickname || ""),
        hasPsi: Boolean(cert.hasPsi),
        fullText: String(cert.fullText || ""),
        idapprl: String(cert.idapprl || parsed?.idApprl || ""),
      },
    };
  } catch {
    return emptySavedCert();
  }
}

export function clearCloudMevCertRecord(storeId: string, envirn: string) {
  if (!storeId || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(uiStorageKey("cert", storeId, envirn));
  } catch {
    // Ignore storage errors.
  }
}

export function saveCloudMevCertRecord(
  storeId: string,
  envirn: string,
  next: { idApprl?: string; status?: string; lastEnrollAt?: string | null; cert?: CloudMevCertInfo | null }
) {
  if (!storeId || typeof localStorage === "undefined") return;
  if (!certLooksInstalled(next.cert)) return;
  try {
    localStorage.setItem(
      uiStorageKey("cert", storeId, envirn),
      JSON.stringify({
        envirn: envStamp(envirn),
        idApprl: String(next.idApprl || next.cert?.idapprl || ""),
        status: String(next.status || "enrolled"),
        lastEnrollAt: String(next.lastEnrollAt || ""),
        cert: next.cert,
      })
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export type CloudMevSavedCsr = {
  pem: string;
  generatedAt: string;
  usedAt: string;
  usedFor: "" | "add" | "replace";
};

function emptySavedCsr(): CloudMevSavedCsr {
  return { pem: "", generatedAt: "", usedAt: "", usedFor: "" };
}

function parseSavedCsr(raw: string): CloudMevSavedCsr {
  const text = String(raw || "").trim();
  if (!text) return emptySavedCsr();
  const meta = extractCsrMeta(text);
  return {
    pem: sanitizeCsrPem(text),
    generatedAt: meta.generatedAt,
    usedAt: meta.usedAt,
    usedFor: meta.usedFor,
  };
}

export function loadSavedCloudMevCsrRecord(storeId: string, envirn: string): CloudMevSavedCsr {
  if (!storeId || typeof localStorage === "undefined") return emptySavedCsr();
  try {
    const raw = localStorage.getItem(uiStorageKey("csr", storeId, envirn)) || "";
    if (!raw.trim()) return emptySavedCsr();
    const parsed = JSON.parse(raw);
    if (!recordMatchesEnv(parsed, envirn)) return emptySavedCsr();
    return parseSavedCsr(raw);
  } catch {
    return emptySavedCsr();
  }
}

export function loadSavedCloudMevCsr(storeId: string, envirn: string): string {
  return loadSavedCloudMevCsrRecord(storeId, envirn).pem;
}

export function saveCloudMevCsrRecord(storeId: string, envirn: string, next: CloudMevSavedCsr) {
  if (!storeId || typeof localStorage === "undefined") return;
  const pem = sanitizeCsrPem(next.pem);
  if (!pem) return;
  try {
    localStorage.setItem(
      uiStorageKey("csr", storeId, envirn),
      JSON.stringify({
        envirn: envStamp(envirn),
        pem,
        generatedAt: next.generatedAt || "",
        usedAt: next.usedAt || "",
        usedFor: next.usedFor || "",
      })
    );
  } catch {
    // Ignore quota / private-mode failures; the in-memory CSR still remains.
  }
}

export function saveCloudMevCsr(storeId: string, envirn: string, pem: string) {
  const prev = loadSavedCloudMevCsrRecord(storeId, envirn);
  const nextPem = sanitizeCsrPem(pem);
  if (!nextPem) return;
  saveCloudMevCsrRecord(storeId, envirn, {
    ...prev,
    pem: nextPem,
    generatedAt: prev.pem === nextPem ? prev.generatedAt : prev.generatedAt || new Date().toISOString(),
  });
}

export function loadSavedCloudMevResponses(storeId: string, envirn: string): CloudMevSavedResponse[] {
  if (!storeId || typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(uiStorageKey("responses", storeId, envirn));
    const parsed = raw ? JSON.parse(raw) : null;
    const items = Array.isArray(parsed) ? [] : recordMatchesEnv(parsed, envirn) ? parsed?.items : [];
    if (!Array.isArray(items)) return [];
    return items
      .map((item: CloudMevSavedResponse) => ({
        id: String(item?.id || ""),
        at: String(item?.at || ""),
        label: String(item?.label || ""),
        text: String(item?.text || ""),
      }))
      .filter((item: CloudMevSavedResponse) => item.id && item.text)
      .slice(0, 50);
  } catch {
    return [];
  }
}

export function saveCloudMevResponses(storeId: string, envirn: string, items: CloudMevSavedResponse[]) {
  if (!storeId || typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(
      uiStorageKey("responses", storeId, envirn),
      JSON.stringify({
        envirn: envStamp(envirn),
        items: items.slice(0, 50),
      })
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}
